import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { CalendarClock, ExternalLink } from 'lucide-react';
import {
  apiMessage,
  getAppointmentCounts,
  getCustomerAppointments,
} from '../services/serviceInboxApi';

/**
 * Appointments — the open ServiceTitan appointments of the email's matched
 * customer, whoever made them.
 *
 * Read from the platform's synced tables through the service inbox, so the
 * list is as fresh as the last sync (minutes in the day), not live. Open means
 * the appointment is not done or canceled and neither is its job.
 *
 * An appointment made in ServiceTitan by someone else is a link to its job and
 * no more: this page neither made it nor changes it. One whose job came from a
 * booking made here says so.
 */

const STATUS_CLASS = {
  Scheduled: 'bg-blue-50 text-blue-800 ring-blue-200',
  Dispatched: 'bg-indigo-50 text-indigo-800 ring-indigo-200',
  Working: 'bg-green-50 text-green-800 ring-green-200',
  Hold: 'bg-amber-50 text-amber-800 ring-amber-200',
};

const STATUS_LABEL = { Hold: 'On hold' };

// Shown in this browser's own time zone, like a booking's start.
const fmtWhen = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'No date';

// The matched customer's appointments, read when the message opens and again
// when the customer changes (a person marks or clears the match). No customer,
// no request.
export const useAppointments = (emailId, customerKey) => {
  const key = emailId != null && customerKey ? `${emailId}|${customerKey}` : null;
  const [state, setState] = useState({ key: null, data: null, error: null, loading: false });
  useEffect(() => {
    if (!key) return undefined;
    let live = true;
    setState({ key, data: null, error: null, loading: true });
    getCustomerAppointments(emailId)
      .then((data) => live && setState({ key, data, error: null, loading: false }))
      .catch(
        (err) =>
          live &&
          setState({
            key,
            data: null,
            error: apiMessage(err, 'Could not read appointments from ServiceTitan.'),
            loading: false,
          })
      );
    return () => {
      live = false;
    };
  }, [key, emailId]);
  // An answer for another customer (the match changed under it) is no answer.
  return state.key === key && key ? state : { key, data: null, error: null, loading: false };
};

// A list row's mark: the icon and the number only, since the customer's name
// and the other marks need the room. The words are on hover.
const Chip = ({ count }) =>
  count ? (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-50 px-2 py-px text-xs text-green-800 ring-1 ring-inset ring-green-200"
      title={`${count} ${count === 1 ? 'appointment' : 'appointments'} open in ServiceTitan for this customer`}
    >
      <CalendarClock className="h-3 w-3" />
      {count}
    </span>
  ) : null;

// --- The list rows' counts ---------------------------------------------------
// Each row asks for its own email, and the asks made while a page draws go out
// as one request. Answers are kept for a couple of minutes, so paging back or
// re-sorting does not ask again. A failed read draws no icons and is retried
// sooner; a list row is no place for an error.

const COUNT_TTL_MS = 120000;
const COUNT_RETRY_MS = 30000;
const COUNT_BATCH = 100;
const COUNT_WAIT_MS = 50;

const counts = new Map(); // "<email id>|<customer>" -> { count, until }
const listeners = new Set();
let pending = new Map(); // key -> email id
let timer = null;

const publish = () => listeners.forEach((fn) => fn());
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const flush = async () => {
  timer = null;
  const asked = [...pending];
  pending = new Map();
  for (let i = 0; i < asked.length; i += COUNT_BATCH) {
    const chunk = asked.slice(i, i + COUNT_BATCH);
    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await getAppointmentCounts(chunk.map(([, emailId]) => emailId));
      const until = Date.now() + COUNT_TTL_MS;
      chunk.forEach(([key, emailId]) =>
        counts.set(key, { count: data?.counts?.[emailId] || 0, until })
      );
    } catch (err) {
      const until = Date.now() + COUNT_RETRY_MS;
      chunk.forEach(([key]) => counts.set(key, { count: 0, until }));
    }
    publish();
  }
};

const ask = (key, emailId) => {
  const have = counts.get(key);
  if ((have && have.until > Date.now()) || pending.has(key)) return;
  pending.set(key, emailId);
  if (!timer) timer = setTimeout(flush, COUNT_WAIT_MS);
};

// For tests: forget every answer and anything waiting to be asked.
export const resetAppointmentCounts = () => {
  counts.clear();
  pending = new Map();
  if (timer) clearTimeout(timer);
  timer = null;
};

export const useAppointmentCount = (emailId, customerKey) => {
  const key = emailId != null && customerKey ? `${emailId}|${customerKey}` : null;
  useEffect(() => {
    if (key) ask(key, emailId);
  }, [key, emailId]);
  return useSyncExternalStore(subscribe, () => (key && counts.get(key)?.count) || 0);
};

// On a list row: the same chip as the card's, from the page's batched count.
export const ListAppointmentChip = ({ emailId, customerKey }) => (
  <Chip count={useAppointmentCount(emailId, customerKey)} />
);

const AppointmentRow = ({ appointment: a }) => {
  const job = [a.job_type, a.job_number && `Job # ${a.job_number}`].filter(Boolean).join(' · ');
  const body = (
    <>
      <CalendarClock className="h-4 w-4 shrink-0 text-gray-500" />
      <span className="tabular-nums text-gray-900">{fmtWhen(a.start)}</span>
      {a.status && (
        <span
          className={`inline-flex shrink-0 items-center rounded-full px-2 py-px text-xs font-medium ring-1 ring-inset ${
            STATUS_CLASS[a.status] || 'bg-gray-100 text-gray-700 ring-gray-200'
          }`}
        >
          {STATUS_LABEL[a.status] || a.status}
        </span>
      )}
      {job && <span className="min-w-0 truncate text-xs text-gray-600">{job}</span>}
      {a.ours && (
        <span
          className="inline-flex shrink-0 items-center rounded-full bg-indigo-50 px-2 py-px text-xs text-indigo-800 ring-1 ring-inset ring-indigo-200"
          title="Its job was made from a booking created here"
        >
          from our booking
        </span>
      )}
    </>
  );
  const className = 'flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded px-1 py-0.5 text-sm';
  return (
    <li>
      {a.servicetitan_url ? (
        <a
          href={a.servicetitan_url}
          target="_blank"
          rel="noopener noreferrer"
          title="Open the job in ServiceTitan"
          className={`${className} hover:bg-black/5`}
        >
          {body}
          <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-xs font-medium text-blue-700">
            Open in ServiceTitan
            <ExternalLink className="h-3 w-3" />
          </span>
        </a>
      ) : (
        <div className={className}>{body}</div>
      )}
    </li>
  );
};

// How many appointments the card links before pointing at the details.
const LINKED = 3;

// A chip has no room for the year; the full date is in the details.
const fmtShort = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'No date';

const LINK_CHIP =
  'inline-flex shrink-0 items-center gap-1 rounded-full bg-green-50 px-2 py-px text-xs text-green-800 ring-1 ring-inset ring-green-200';

// On the card's always-shown line, beside the customer and the task: each open
// appointment as a chip that opens its job in ServiceTitan, so a visit already
// on the books is seen and opened without expanding anything. Nothing when the
// customer has none.
export const AppointmentLinks = ({ appointments }) => {
  const rows = appointments?.data?.appointments || [];
  if (!rows.length) return null;
  const more = rows.length - LINKED;
  return (
    <>
      {rows.slice(0, LINKED).map((a) => {
        const body = (
          <>
            <CalendarClock className="h-3 w-3" />
            {fmtShort(a.start)}
            {a.status ? ` · ${STATUS_LABEL[a.status] || a.status}` : ''}
          </>
        );
        return a.servicetitan_url ? (
          <a
            key={a.appointment_id}
            href={a.servicetitan_url}
            target="_blank"
            rel="noopener noreferrer"
            title="Open this appointment's job in ServiceTitan"
            className={`${LINK_CHIP} hover:bg-green-100`}
          >
            {body}
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <span key={a.appointment_id} className={LINK_CHIP} title="An open appointment in ServiceTitan">
            {body}
          </span>
        );
      })}
      {more > 0 && (
        <span className={LINK_CHIP} title="The rest are under Appointment in the details">
          +{more}
          {appointments.data.truncated ? '+' : ''} more
        </span>
      )}
    </>
  );
};

// In the Appointment section, under the booking. Draws nothing for an email
// with no customer to ask about.
export const AppointmentList = ({ appointments }) => {
  const { data, error, loading } = appointments || {};
  if (!data && !error && !loading) return null;
  const rows = data?.appointments || [];
  return (
    <div className="mt-3 border-t border-gray-200 pt-2">
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        In ServiceTitan
      </div>
      {loading && <div className="text-sm text-gray-500">Reading appointments…</div>}
      {error && <div className="text-sm text-red-700">{error}</div>}
      {data && !rows.length && (
        <div className="text-sm text-gray-500">No open appointments in ServiceTitan.</div>
      )}
      {rows.length > 0 && (
        <ul className="-mx-1 space-y-0.5">
          {rows.map((a) => (
            <AppointmentRow key={a.appointment_id} appointment={a} />
          ))}
        </ul>
      )}
      {data?.truncated && (
        <div className="mt-1 text-xs text-gray-500">
          Showing the first {rows.length}; this customer has more.
        </div>
      )}
    </div>
  );
};
